import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, History, Layers, Pencil, RefreshCw } from 'lucide-react';
import { dynamicFieldsApi } from './api';
import { LibraryAuditLogItem } from './types';
import { useLibraryRef } from './useLibraryRef';
import { Change, ChangeList } from '../components/ChangeList';
import { secondaryButton, Skeleton } from '../ui';
import { ROUTES } from '../../../config/links';

const PAGE_SIZE = 50;
const ACTIONS: LibraryAuditLogItem['action'][] = ['draft_created', 'draft_saved', 'published', 'rolled_back', 'draft_discarded'];
const ACTION_STYLE: Record<LibraryAuditLogItem['action'], string> = {
  draft_created: 'bg-emerald-100 text-emerald-700',
  draft_saved: 'bg-amber-100 text-amber-700',
  draft_discarded: 'bg-red-100 text-red-700',
  published: 'bg-violet-100 text-violet-700',
  rolled_back: 'bg-orange-100 text-orange-700',
};
const label = (action: string) => action.replace('_', ' ');

const summarize = (log: LibraryAuditLogItem): string => {
  const d = log.details || {};
  switch (log.action) {
    case 'draft_created': return `Draft v${log.version ?? '?'} created (${d.field_count ?? 0} entries)`;
    case 'draft_saved': return `Draft v${log.version ?? '?'} saved (${d.field_count ?? 0} entries)`;
    case 'draft_discarded': return `Draft v${log.version ?? '?'} discarded`;
    case 'published': return `Version ${log.version ?? '?'} published${d.previous_version ? `, replacing v${d.previous_version}` : ''}`;
    case 'rolled_back': return `Rolled back to v${d.restored_from ?? '?'} (new v${log.version ?? '?'}); previous live was v${d.previous_version ?? '?'}`;
    default: return '';
  }
};

/** Every draft, publish and rollback of one dynamic field library, newest first. */
export default function DynamicFieldsAuditPage() {
  const ref = useLibraryRef();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<LibraryAuditLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState<LibraryAuditLogItem['action'] | 'all'>('all');

  const load = useCallback((reset: boolean, offset = 0) => {
    if (!ref) return;
    setLoading(true);
    dynamicFieldsApi.auditLogs(ref, { limit: PAGE_SIZE, offset })
      .then((rows) => {
        setLogs((current) => (reset ? rows : [...current, ...rows]));
        setHasMore(rows.length === PAGE_SIZE);
      })
      .catch((e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || e?.message || 'Failed to load audit logs'))
      .finally(() => setLoading(false));
  }, [ref]);
  useEffect(() => { load(true); }, [load]);

  if (!ref) return <div className="grid min-h-full place-items-center bg-slate-50 p-8 text-sm text-slate-500">Choose a library from the <button className="ml-1 font-medium text-primary-700 underline" onClick={() => navigate(ROUTES.DYNAMIC_FIELDS)}>dynamic fields list</button>.</div>;

  const visible = filter === 'all' ? logs : logs.filter((l) => l.action === filter);

  return (
    <div className="min-h-full bg-slate-50 p-3 md:p-8">
      <header className="mb-4 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600"><History className="h-3.5 w-3.5" /> Audit trail</div>
          <h1 className="mt-1 text-xl font-semibold tracking-tight text-slate-900">{ref.sensor_type} <span className="text-slate-300">/</span> {ref.category}</h1>
          <p className="mt-1 text-xs text-slate-500">Every draft, publish and rollback of this dynamic field library, newest first.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className={secondaryButton} onClick={() => load(true)} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          <button className={secondaryButton} onClick={() => navigate(ROUTES.dynamicFieldsEdit(ref.sensor_type, ref.category))}><Pencil className="h-3.5 w-3.5" /> Edit</button>
          <button className={secondaryButton} onClick={() => navigate(ROUTES.dynamicFieldsVersions(ref.sensor_type, ref.category))}><Layers className="h-3.5 w-3.5" /> Versions</button>
          <button className={secondaryButton} onClick={() => navigate(ROUTES.DYNAMIC_FIELDS)}><ArrowLeft className="h-3.5 w-3.5" /> All libraries</button>
        </div>
      </header>

      <div className="mb-3 flex flex-wrap gap-1.5">
        {(['all', ...ACTIONS] as const).map((a) => (
          <button key={a} type="button" onClick={() => setFilter(a)}
            className={`rounded-full px-3 py-1 text-[11px] font-medium capitalize transition ${filter === a ? 'bg-slate-800 text-white' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
            {label(a)}
          </button>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[720px] text-left text-[12px]">
          <thead className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-2.5">When</th><th className="px-4 py-2.5">Action</th><th className="px-4 py-2.5">Version</th><th className="px-4 py-2.5">User</th><th className="px-4 py-2.5">Details</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && logs.length === 0 && Array.from({ length: 6 }).map((_, i) => (
              <tr key={i} aria-hidden="true">{Array.from({ length: 5 }).map((__, j) => <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>)}</tr>
            ))}
            {!loading && visible.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No audit entries{filter !== 'all' ? ` for “${label(filter)}”` : ''}.</td></tr>}
            {visible.map((log, index) => (
              <tr key={`${log.created_at}-${index}`} className="align-top hover:bg-slate-50/60">
                <td className="whitespace-nowrap px-4 py-3 text-slate-600">{new Date(log.created_at).toLocaleString()}</td>
                <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${ACTION_STYLE[log.action] || 'bg-slate-100 text-slate-600'}`}>{label(log.action)}</span></td>
                <td className="px-4 py-3 text-slate-600">{log.version != null ? `v${log.version}` : '—'}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-700">{log.actor_name || 'Unknown'}</div>
                  {log.actor_id && <div className="font-mono text-[10px] text-slate-400">{log.actor_id}</div>}
                </td>
                <td className="px-4 py-3 text-slate-600">
                  <div>{summarize(log) || '—'}</div>
                  {Array.isArray(log.details?.changes) && log.details!.changes.length > 0 && <ChangeList changes={log.details!.changes as Change[]} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && <div className="mt-3 flex justify-center"><button className={secondaryButton} onClick={() => load(false, logs.length)} disabled={loading}>{loading ? 'Loading…' : 'Load more'}</button></div>}
    </div>
  );
}
